import { appLanguage } from '@orientaction/reducers';
import { blockRedirection, publicSvgPath } from '@orientaction/utils';
import { ChatTeardropText } from '@phosphor-icons/react';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import SimpleModal from '../../../../../../components/SimpleModal';
import RedesignImage from '../../../../shared/components/RedesignImage';
import { GoFurtherConfig } from '../types';
import { useGoFurtherSectionStyles } from './GoFurtherSection.styles';

type GoFurtherSectionProps = {
  config: GoFurtherConfig;
  onContactClick: () => void;
};

const DEFAULT_SHOP_TITLE = 'Acheter le jeu de cartes des valeurs';
const DEFAULT_SHOP_BUTTON = 'Acheter le jeu';
const DEFAULT_SHOP_LINES = [
  'Découvrez le jeu de cartes des valeurs.',
  'Il permet d’identifier ses valeurs profondes ou celles de ses proches.',
  '3 € de réduction avec le code PROMO3EUROS.',
];
const DEFAULT_CONTACT_TITLE = 'CONTACTEZ NOTRE SERVICE DE FORMATION';
const DEFAULT_CONTACT_DESCRIPTION =
  'Pour organiser un atelier ou une formation sur les valeurs dans votre entreprise';
const DEFAULT_SHOP_URL = 'https://boutique-orientaction.com/products/jeu-de-cartes-des-valeurs';

const splitDescriptionLines = (text: string): string[] => {
  if (text.includes('\n')) {
    return text.split('\n').filter(Boolean);
  }

  const sentences = text.split(/(?<=\.)\s+/).filter(Boolean);
  return sentences.length > 0 ? sentences : [text];
};

const GoFurtherSection = ({ config, onContactClick }: GoFurtherSectionProps) => {
  const classes = useGoFurtherSectionStyles();
  const lang: string = useSelector(appLanguage);
  const [openModal, setOpenModal] = useState(false);

  const shopImage =
    config.shopImage ||
    `${publicSvgPath}/${lang === 'FR' ? 'jeu_cartes.png' : 'en_jeu-cartes.png'}`;
  const shopUrl = config.shopUrl || DEFAULT_SHOP_URL;

  const shopDescriptionLines = useMemo(() => {
    if (!config.shopDescription) {
      return DEFAULT_SHOP_LINES;
    }
    return splitDescriptionLines(config.shopDescription);
  }, [config.shopDescription]);

  const openUrl = (url: string, skipLanguageGate = false) => {
    if (skipLanguageGate) {
      window.open(url, '_blank');
      return;
    }

    blockRedirection({
      language: lang,
      onOpen: () => setOpenModal(true),
      functionToPass: () => {
        window.open(url, '_blank');
      },
    });
  };

  const showShop = Boolean(config.showShopCard);

  return (
    <section className={classes.goFurtherSection} aria-labelledby="go-further-title">
      <div
        className={clsx(
          classes.goFurtherContainer,
          !showShop && classes.goFurtherContainerContactOnly,
        )}>
        <h2 id="go-further-title" className={classes.goFurtherTitle}>
          Pour aller plus loin
        </h2>

        <div
          className={clsx(
            classes.goFurtherContent,
            !showShop && classes.goFurtherContentContactOnly,
          )}>
          {showShop && (
            <div className={classes.goFurtherShopColumn}>
              <p className={classes.goFurtherShopTitle}>
                {config.shopTitle || DEFAULT_SHOP_TITLE}
              </p>
              <RedesignImage
                src={shopImage}
                alt={config.shopImageAlt || DEFAULT_SHOP_TITLE}
                className={classes.goFurtherShopImage}
              />
              <div className={classes.goFurtherShopDescription}>
                {shopDescriptionLines.map((line, index) => (
                  <p
                    key={line}
                    className={clsx(
                      classes.goFurtherShopDescriptionLine,
                      config.shopDescriptionSemiboldLines?.includes(index) &&
                        classes.goFurtherShopDescriptionLineEmphasis,
                    )}>
                    {line}
                  </p>
                ))}
              </div>
              <button
                type="button"
                className={classes.goFurtherShopButton}
                onClick={() => openUrl(shopUrl, config.shopDirectOpen)}>
                {config.shopButtonLabel || DEFAULT_SHOP_BUTTON}
              </button>
              {config.shopSecondaryLabel && config.shopSecondaryUrl && (
                <button
                  type="button"
                  className={classes.goFurtherShopSecondary}
                  onClick={() => openUrl(config.shopSecondaryUrl as string)}>
                  {config.shopSecondaryLabel}
                </button>
              )}
            </div>
          )}

          <div className={classes.goFurtherContactCard}>
            <div className={classes.goFurtherContactInner}>
              <ChatTeardropText
                size={48}
                aria-hidden={true}
                className={classes.goFurtherContactIcon}
              />
              <div className={classes.goFurtherContactText}>
                <p className={classes.goFurtherContactTitle}>
                  {config.contactTitle || DEFAULT_CONTACT_TITLE}
                </p>
                <p className={classes.goFurtherContactDescription}>
                  {config.contactDescription || DEFAULT_CONTACT_DESCRIPTION}
                </p>
              </div>
            </div>
            <button type="button" className={classes.goFurtherContactButton} onClick={onContactClick}>
              Nous contacter
            </button>
          </div>
        </div>
      </div>

      <SimpleModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        title="Information"
        message="Not yet available in English – work in progress."
      />
    </section>
  );
};

export default GoFurtherSection;
